import * as React from 'react';
import { 
  Box, 
  Tab, 
  Tabs, 
  useTheme, 
  useMediaQuery,
  Container
} from '@mui/material';
import './MenuTabs.css';

import LandingPage from '../landing/LandingPage';
import AboutUs from '../aboutus/AboutUs';
import ApplicationStatus from '../../components/applicationstatus/applicationstatus';
import ContactUs from '../contactus/ContactUs';

// Custom styles for tabs
const customTabStyles = {
  tabsContainer: {
    position: 'sticky',
    top: 0,
    zIndex: 100,
    backgroundColor: 'white',
    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
    width: '100%'
  },
  tabsScrollable: {
    overflowX: 'auto',
    overflowY: 'hidden',
    scrollbarWidth: 'thin',
    scrollbarColor: '#26516F rgba(0,0,0,0.1)'
  },
  tabsRoot: {
    width: '100%',
    minHeight: '48px'
  },
  tabsIndicator: {
    backgroundColor: '#26516F',
    height: '3px',
    borderRadius: '0'
  },
  tabsFlexContainer: {
    justifyContent: 'center',
    gap: '16px'
  },
  tabsFlexContainerMobile: {
    justifyContent: 'flex-start',
    gap: '0px',
    width: 'max-content',
    minWidth: '100%'
  },
  tabRoot: {
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '1rem',
    fontFamily: '"Montserrat", "Roboto", "Helvetica", "Arial", sans-serif',
    color: '#26516F',
    minHeight: '48px',
    padding: '12px 16px',
    transition: 'all 0.3s ease',
    borderRadius: '0',
    backgroundColor: 'transparent'
  },
  tabRootMobile: {
    minWidth: '120px',
    fontSize: '0.85rem',
    padding: '8px 12px',
    whiteSpace: 'nowrap',
    flexShrink: 0
  },
  tabHover: {
    backgroundColor: 'transparent',
    transform: 'translateY(-1px)'
  },
  tabSelected: {
    color: '#26516F',
    fontWeight: 800,
    backgroundColor: 'transparent'
  },
  scrollButtons: {
    color: '#26516F'
  },
  scrollButtonsDisabled: {
    opacity: 0.3
  }
};

// Custom TabPanel component
function TabPanel({ children, value, index, ...other }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const tabPanelStyles = {
    paddingTop: isMobile ? '16px' : '32px',
    paddingBottom: isMobile ? '16px' : '32px'
  };
  
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`kabahagi-tabpanel-${index}`}
      aria-labelledby={`kabahagi-tab-${index}`}
      {...other}
    >
      {value === index && (
        <div style={tabPanelStyles}>
          {children}
        </div>
      )}
    </div>
  );
}

export default function MenuTabsSSDD() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [value, setValue] = React.useState(0);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const tabLabels = [
    'SERVICES',
    'APPLICATION STATUS', 
    'ABOUT US',
    'CONTACT US'
  ];

  // Dynamic styles based on mobile state
  const containerStyles = {
    paddingLeft: isMobile ? '8px' : '24px', 
    paddingRight: isMobile ? '8px' : '24px'
  };

  const tabsContainerStyle = {
    ...customTabStyles.tabsContainer,
    ...(isMobile ? customTabStyles.tabsScrollable : {})
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Sticky tabs header */}
      <div style={tabsContainerStyle}>
        <Container maxWidth="xl" style={containerStyles}>
          <Tabs
            value={value}
            onChange={handleChange}
            variant={isMobile ? "scrollable" : "standard"}
            scrollButtons={isMobile ? "auto" : false}
            allowScrollButtonsMobile={isMobile}
            aria-label="kabahagi navigation tabs"
            className="kabahagi-tabs-root"
            style={customTabStyles.tabsRoot}
            TabIndicatorProps={{
              style: customTabStyles.tabsIndicator,
              className: "kabahagi-tabs-indicator"
            }}
            sx={{
              '& .MuiTabs-flexContainer': {
                ...(isMobile ? customTabStyles.tabsFlexContainerMobile : customTabStyles.tabsFlexContainer)
              },
              '& .MuiTabs-scroller': {
                ...(isMobile && {
                  overflow: 'auto !important'
                })
              },
              '& .MuiTabs-scrollButtons': {
                ...customTabStyles.scrollButtons,
                '&.Mui-disabled': customTabStyles.scrollButtonsDisabled
              },
              '& .MuiTabs-indicator': {
                border: 'none !important',
                borderRadius: '0 !important',
                boxShadow: 'none !important'
              },
              '& .MuiTab-root': {
                border: 'none !important',
                borderRadius: '0 !important',
                backgroundColor: 'transparent !important',
                '&:hover': {
                  border: 'none !important',
                  backgroundColor: 'transparent !important'
                },
                '&.Mui-selected': {
                  border: 'none !important',
                  backgroundColor: 'transparent !important'
                }
              }
            }}
          >
            {tabLabels.map((label, index) => {
              const tabStyle = {
                ...customTabStyles.tabRoot,
                ...(isMobile ? customTabStyles.tabRootMobile : {})
              };
              
              return (
                <Tab
                  key={index}
                  label={label}
                  id={`kabahagi-tab-${index}`}
                  aria-controls={`kabahagi-tabpanel-${index}`}
                  className={`kabahagi-tab ${value === index ? 'kabahagi-tab-selected' : ''}`}
                  style={tabStyle}
                  onMouseEnter={(e) => {
                    if (value !== index) {
                      Object.assign(e.target.style, {
                        ...customTabStyles.tabHover,
                        backgroundColor: 'transparent'
                      });
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (value !== index) {
                      Object.assign(e.target.style, tabStyle);
                    }
                  }}
                  sx={{
                    border: 'none !important',
                    borderRadius: '0 !important',
                    backgroundColor: 'transparent !important',
                    '&:hover': {
                      border: 'none !important',
                      backgroundColor: 'transparent !important',
                      ...customTabStyles.tabHover
                    },
                    '&.Mui-selected': {
                      border: 'none !important',
                      backgroundColor: 'transparent !important',
                      ...customTabStyles.tabSelected
                    },
                    '&:focus': {
                      border: 'none !important',
                      backgroundColor: 'transparent !important'
                    }
                  }}
                />
              );
            })}
          </Tabs>
        </Container>
      </div>

      {/* Tab content */}
      <Container maxWidth="xl" style={containerStyles}>
        <TabPanel value={value} index={0}>
          <LandingPage />
        </TabPanel>
        <TabPanel value={value} index={1}>
          <ApplicationStatus />
        </TabPanel>
        <TabPanel value={value} index={2}>
          <AboutUs />
        </TabPanel>
        <TabPanel value={value} index={3}>
          <ContactUs />
        </TabPanel>
      </Container>
    </Box>
  );
}